// src/ThreeJs/SetUps/enableSphereDrawing.js
import * as THREE from 'three';
import { TREESPHERES, VARS } from '../Global/variables';
import { pushUndoAction } from "../Global/UndoManager";
import { notifyIrradianceNeedsRecompute } from "../Shading/IrradianceEvents";

export function createTrimSphereMesh({
  position,
  scale,
  locked = false,
  enabled = true,
  name,
} = {}) {
  const sphere = new THREE.Mesh(
    new THREE.SphereGeometry(1, 48, 48),
    new THREE.MeshStandardMaterial({
      color: 0xff4d4f,
      emissive: 0x4a0d10,
      metalness: 0.1,
      roughness: 0.4,
      opacity: 0.34,
      transparent: true,
    })
  );

  if (position) sphere.position.set(position.x || 0, position.y || 0, position.z || 0);
  if (scale) {
    sphere.scale.set(
      typeof scale.x === "number" ? scale.x : 1,
      typeof scale.y === "number" ? scale.y : 1,
      typeof scale.z === "number" ? scale.z : 1
    );
  }

  sphere.name = name || `InteractiveSphere_${Date.now()}`;
  sphere.userData.mode = "translate";
  sphere.userData.locked = !!locked;
  sphere.userData.enabled = enabled !== false;
  sphere.renderOrder = 2;
  sphere.material.depthWrite = false;

  return sphere;
}

export function getExclusionSphereConfigs({ enabledOnly = false } = {}) {
  return (TREESPHERES?.spheres || [])
    .filter((sphere) => sphere && sphere.isObject3D)
    .filter((sphere) => (enabledOnly ? sphere.userData?.enabled !== false : true))
    .map((sphere) => ({
      id: sphere.uuid,
      position: {
        x: sphere.position.x,
        y: sphere.position.y,
        z: sphere.position.z,
      },
      scale: {
        x: sphere.scale.x,
        y: sphere.scale.y,
        z: sphere.scale.z,
      },
      locked: !!sphere.userData?.locked,
      enabled: sphere.userData?.enabled !== false,
    }));
}

export function setupSphereControls(scene, camera, renderer, setRemovedSections) {
  let activeSphere = null;
  let isDragging = false;
  let sphereDragMoved = false;
  let sphereDragStartState = null;
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  const dragPlane = new THREE.Plane();
  const dragOffset = new THREE.Vector3();
  const handlesBySphere = new Map();
  const sphereByHandle = new WeakMap();
  let listenersBound = false;

  const setCursor = (value) => {
    if (renderer?.domElement) renderer.domElement.style.cursor = value;
  };

  const captureSphereState = (sphere) => {
    if (!sphere) return null;
    return {
      position: sphere.position.clone(),
      scale: sphere.scale.clone(),
      locked: !!sphere.userData?.locked,
      enabled: sphere.userData?.enabled !== false,
    };
  };

  const applySphereState = (sphere, state) => {
    if (!sphere || !state) return;
    sphere.position.copy(state.position);
    sphere.scale.copy(state.scale);
    sphere.userData.locked = state.locked;
    sphere.userData.enabled = state.enabled;
  };

  const isTreeUiActive = () => {
    if (typeof window === "undefined") return true;
    return window.isTreeUiActive === true;
  };

  const isIrradianceBusy = () => {
    if (typeof window === "undefined") return false;
    return window.isIrradianceBusy === true;
  };

  const emitSphereSelected = (sphere) => {
    // Mirrors VARS.selectedTree: the keyboard handler needs to know which
    // excluder is selected to route a Delete press to it.
    VARS.selectedSphere = sphere || null;
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent("TRIM_SPHERE_SELECTED", { detail: { sphere: sphere || null } })
    );
  };

  const getActiveSpheres = () =>
    (TREESPHERES?.spheres || []).filter((sphere) => sphere && sphere.isObject3D);

  const toNdc = (event) => {
    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  };

  const removeHandle = (sphere) => {
    const handle = handlesBySphere.get(sphere);
    if (!handle) return;
    if (handle.parent) handle.parent.remove(handle);
    handle.geometry?.dispose?.();
    handle.material?.dispose?.();
    handlesBySphere.delete(sphere);
  };

  const ensureHandle = (sphere) => {
    if (!sphere) return null;
    const existing = handlesBySphere.get(sphere);
    if (existing) return existing;

    const handleSize = 0.08;
    const centerHandle = new THREE.Mesh(
      new THREE.BoxGeometry(handleSize, handleSize, handleSize),
      new THREE.MeshBasicMaterial({
        color: 0xffeb3b,
        transparent: true,
        opacity: 0.92,
        depthTest: false,
      })
    );

    centerHandle.name = "TrimSphereCenterHandle";
    centerHandle.visible = false;
    centerHandle.renderOrder = 5;
    sphere.add(centerHandle);
    handlesBySphere.set(sphere, centerHandle);
    sphereByHandle.set(centerHandle, sphere);

    return centerHandle;
  };

  const updateHandleVisibility = (hoveredSphere = null, hoveredHandleSphere = null) => {
    const treeUiActive = isTreeUiActive();
    handlesBySphere.forEach((handle, sphere) => {
      if (!sphere || !sphere.parent) {
        removeHandle(sphere);
        return;
      }
      const sphereLocked = !!sphere.userData?.locked;
      const isSelected = activeSphere === sphere;
      const isHovered = hoveredSphere === sphere || hoveredHandleSphere === sphere;
      handle.visible = treeUiActive && !sphereLocked && (isSelected || isHovered);
    });
  };

  const findSphereFromIntersectionObject = (obj) => {
    if (!obj) return null;
    if (sphereByHandle.has(obj)) return sphereByHandle.get(obj);

    let cursor = obj;
    while (cursor) {
      if (handlesBySphere.has(cursor)) return cursor;
      if (getActiveSpheres().includes(cursor)) return cursor;
      cursor = cursor.parent;
    }
    return null;
  };

  const syncHandlesWithSpheres = () => {
    const spheres = getActiveSpheres();
    spheres.forEach((sphere) => ensureHandle(sphere));
    Array.from(handlesBySphere.keys()).forEach((sphere) => {
      if (!spheres.includes(sphere)) removeHandle(sphere);
    });
  };

  const stopDrag = () => {
    if (!isDragging) return;
    isDragging = false;
    if (sphereDragMoved) {
      const targetSphere = activeSphere;
      const before = sphereDragStartState;
      const after = captureSphereState(targetSphere);
      if (targetSphere && before && after) {
        pushUndoAction({
          label: "Move sphere",
          undo: () => {
            applySphereState(targetSphere, before);
            setRemovedSections?.((prev) => ({ ...prev, spheres: [...(prev?.spheres || [])] }));
            notifyIrradianceNeedsRecompute("trim-sphere-moved");
          },
          redo: () => {
            applySphereState(targetSphere, after);
            setRemovedSections?.((prev) => ({ ...prev, spheres: [...(prev?.spheres || [])] }));
            notifyIrradianceNeedsRecompute("trim-sphere-moved");
          },
        });
      }
      notifyIrradianceNeedsRecompute("trim-sphere-moved");
    }
    sphereDragStartState = null;
    sphereDragMoved = false;
    const orbit = scene.userData.orbitControls;
    if (orbit) orbit.enabled = true;
    setCursor("default");
  };

  const onPointerMove = (event) => {
    toNdc(event);
    raycaster.setFromCamera(mouse, camera);

    if (isDragging) {
      if (isIrradianceBusy()) {
        stopDrag();
        return;
      }
      if (!activeSphere) return;
      const next = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(dragPlane, next)) {
        const target = next.sub(dragOffset);
        if (activeSphere.position.distanceToSquared(target) > 1e-10) {
          sphereDragMoved = true;
        }
        activeSphere.position.copy(target);
      }
      return;
    }

    const spheres = getActiveSpheres();
    syncHandlesWithSpheres();
    if (!spheres.length) {
      setCursor("default");
      return;
    }

    const handles = Array.from(handlesBySphere.values());
    const sphereHits = raycaster.intersectObjects(spheres, true);
    const handleHits = handles.length ? raycaster.intersectObjects(handles, false) : [];

    const hoveredSphere = sphereHits.length
      ? findSphereFromIntersectionObject(sphereHits[0].object)
      : null;
    const hoveredHandleSphere = handleHits.length
      ? findSphereFromIntersectionObject(handleHits[0].object)
      : null;

    updateHandleVisibility(hoveredSphere, hoveredHandleSphere);

    const canMoveHoveredHandle =
      !!hoveredHandleSphere &&
      !hoveredHandleSphere.userData?.locked &&
      isTreeUiActive();
    setCursor(canMoveHoveredHandle ? "grab" : "default");
  };

  const onPointerDown = (event) => {
    toNdc(event);
    raycaster.setFromCamera(mouse, camera);

    const spheres = getActiveSpheres();
    syncHandlesWithSpheres();
    if (!spheres.length) return;
    const handles = Array.from(handlesBySphere.values());
    const handleHits = handles.length ? raycaster.intersectObjects(handles, false) : [];
    const sphereHits = raycaster.intersectObjects(spheres, true);
    const targetSphere = handleHits.length
      ? findSphereFromIntersectionObject(handleHits[0].object)
      : sphereHits.length
      ? findSphereFromIntersectionObject(sphereHits[0].object)
      : null;
    if (!targetSphere) {
      // Clicking away clears the selection, so a stale excluder can never
      // absorb a Delete press meant for something else.
      if (activeSphere) {
        activeSphere = null;
        emitSphereSelected(null);
        updateHandleVisibility();
      }
      return;
    }

    activeSphere = targetSphere;
    emitSphereSelected(targetSphere);
    updateHandleVisibility(targetSphere, handleHits.length ? targetSphere : null);

    if (isIrradianceBusy() || !isTreeUiActive() || targetSphere.userData?.locked) return;
    if (handleHits.length === 0) return;

    const orbit = scene.userData.orbitControls;
    if (orbit) orbit.enabled = false;

    const planeNormal = new THREE.Vector3();
    camera.getWorldDirection(planeNormal);
    dragPlane.setFromNormalAndCoplanarPoint(planeNormal, targetSphere.position);

    const hitPoint = new THREE.Vector3();
    if (raycaster.ray.intersectPlane(dragPlane, hitPoint)) {
      dragOffset.copy(hitPoint).sub(targetSphere.position);
    } else {
      dragOffset.set(0, 0, 0);
    }

    isDragging = true;
    sphereDragStartState = captureSphereState(targetSphere);
    sphereDragMoved = false;
    setCursor("grabbing");
  };

  const onPointerUp = () => {
    stopDrag();
  };

  const bindPointerListeners = () => {
    if (listenersBound) return;
    renderer.domElement.addEventListener("pointermove", onPointerMove);
    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointerup", onPointerUp);
    listenersBound = true;
  };

  const unbindPointerListeners = () => {
    if (!listenersBound) return;
    renderer.domElement.removeEventListener("pointermove", onPointerMove);
    renderer.domElement.removeEventListener("pointerdown", onPointerDown);
    window.removeEventListener("pointerup", onPointerUp);
    listenersBound = false;
  };

  function attachControlsToSphere(sphere) {
    if (!sphere) return;

    activeSphere = sphere;
    VARS.selectedSphere = sphere;
    ensureHandle(sphere);
    updateHandleVisibility(sphere, null);

    if (scene.userData.orbitControls) window.orbitControls = scene.userData.orbitControls;
    bindPointerListeners();
  }

  window.attachControlsToSphere = attachControlsToSphere;

  window.removeSphereAndControls = (sphere, options = {}) => {
    if (!sphere) return;
    const recordUndo = options?.recordUndo !== false;
    const originalIndex = TREESPHERES?.spheres?.indexOf(sphere);
    const wasActive = activeSphere === sphere;
    const parent = sphere.parent || scene;

    if (wasActive) {
      stopDrag();
      activeSphere = null;
    }
    if (VARS.selectedSphere === sphere) VARS.selectedSphere = null;

    removeHandle(sphere);
    if (handlesBySphere.size === 0) {
      unbindPointerListeners();
    } else {
      updateHandleVisibility();
    }

    scene.remove(sphere);
    notifyIrradianceNeedsRecompute("trim-sphere-removed");

    if (TREESPHERES?.spheres?.length) {
      const idx = TREESPHERES.spheres.indexOf(sphere);
      if (idx !== -1) TREESPHERES.spheres.splice(idx, 1);
    }

    setRemovedSections?.((prev) => ({
      ...prev,
      spheres: (prev?.spheres || []).filter((s) => s !== sphere),
    }));

    if (window.orbitControls) window.orbitControls.enabled = true;
    setCursor("default");

    if (recordUndo) {
      pushUndoAction({
        label: "Delete sphere",
        undo: () => {
          if (!sphere.parent) parent.add(sphere);
          if (!TREESPHERES.spheres.includes(sphere)) {
            const insertAt = Math.max(0, originalIndex);
            TREESPHERES.spheres.splice(insertAt, 0, sphere);
          }
          setRemovedSections?.((prev) => ({
            ...prev,
            spheres: [...(prev?.spheres || []), sphere],
          }));
          notifyIrradianceNeedsRecompute("trim-sphere-removed");
        },
        redo: () => {
          window.removeSphereAndControls?.(sphere, { recordUndo: false });
        },
      });
    }
  };

  // Keep sphere hit-testing active even before any sphere is selected.
  bindPointerListeners();

  return { attachControlsToSphere };
}

export function enableSphereDrawing(scene, camera, renderer, targetMeshes, setRemovedSections) {
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  let drawingMode = true;

  console.log("✅ Sphere drawing mode enabled.");

  const { attachControlsToSphere } = setupSphereControls(
    scene,
    camera,
    renderer,
    setRemovedSections
  );

  // handle click to create sphere
  function onClick(event) {
    if (typeof window !== "undefined" && window.isIrradianceBusy === true) return;
    if (!drawingMode) return;
    drawingMode = false;

    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObjects(
      Array.isArray(targetMeshes) ? targetMeshes : [targetMeshes],
      true
    );
    if (intersects.length === 0) return;
    const point = intersects[0].point;

    const sphere = createTrimSphereMesh({
      position: point,
      scale: { x: 1, y: 1, z: 1 },
      locked: false,
      enabled: true,
    });
    scene.add(sphere);

    TREESPHERES.spheres.push(sphere);
    setRemovedSections?.((prev) => ({
      ...prev,
      spheres: [...(prev?.spheres || []), sphere],
    }));
    notifyIrradianceNeedsRecompute("trim-sphere-created");

    pushUndoAction({
      label: "Add sphere",
      undo: () => {
        window.removeSphereAndControls?.(sphere, { recordUndo: false });
      },
      redo: () => {
        if (!sphere.parent) scene.add(sphere);
        if (!TREESPHERES.spheres.includes(sphere)) TREESPHERES.spheres.push(sphere);
        setRemovedSections?.((prev) => ({
          ...prev,
          spheres: [...(prev?.spheres || []), sphere],
        }));
        notifyIrradianceNeedsRecompute("trim-sphere-created");
      },
    });

    attachControlsToSphere(sphere);
  }

  renderer.domElement.addEventListener("click", onClick, { once: true });
}
